import { useWorkspaceId } from "../../hooks/useWorkspaceId";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useHttpTaxManagerAppHeaders } from "../utils/useHttpTaxManagerAppHeaders";
import { BASE_API_URL, HttpError } from "../utils/useTaxManagerAppQueryClient";

interface AddCustomFulfilmentRequest {
    deadline: Date;
    fulfilmentName: string;
    workspaceIds: string[];
}

export enum ResponseStatus {
    OK = 201,
    CONFLICT = 409,
    INTERNAL_SERVER_ERROR = 500,
}

export function useAddCustomFulfilment() {
    const { t } = useTranslation();
    const workspaceId = useWorkspaceId();
    const headers = useHttpTaxManagerAppHeaders({ method: "POST" });
    const queryClient = useQueryClient();

    const mutation = useMutation<
        ResponseStatus,
        Error,
        AddCustomFulfilmentRequest
    >({
        mutationFn: async (request) => {
            const response = await fetch(
                `${BASE_API_URL}/tenants/${workspaceId}/fulfilments/custom`,
                {
                    headers,
                    method: "POST",
                    body: JSON.stringify({
                        deadline: dayjs(request.deadline)
                            .hour(12)
                            .minute(0)
                            .second(0)
                            .millisecond(0)
                            .toISOString(),
                        fulfilmentName: request.fulfilmentName,
                        workspaceIds: request.workspaceIds,
                    }),
                },
            );

            if (response.status === ResponseStatus.CONFLICT) {
                return ResponseStatus.CONFLICT;
            }

            if (!response.ok) {
                throw new HttpError(response);
            }

            return ResponseStatus.OK;
        },
        onSuccess: (status) => {
            if (status !== ResponseStatus.OK) return;
            queryClient.invalidateQueries({ queryKey: ["useGetFulfilments"] });
            queryClient.invalidateQueries({
                queryKey: ["useGetLinkableFulfilments"],
            });
            queryClient.invalidateQueries({ queryKey: ["useGetKPIs"] });
        },
        meta: {
            errorMessage: t("errors.messages.addCustomFulfilment"),
        },
    });

    return mutation;
}
